import { withAssignmentRequestDeadline } from '../utils/assignmentRequestDeadline';

// One transport lane per account/assignment, shared by title and full writes.
// A rejected operation releases the lane after the actual transport settles;
// its caller still receives the error and retains its unsaved edits.
const lanes = new Map();
const keyFor = (userId, assignmentId) => JSON.stringify([userId, assignmentId]);
// Writes whose failure the caller fully handles itself (the question coordinator
// keeps the unsaved title and offers a retry; a rename reverts its own row), so a
// failed one must not make a later drain report the lane as unsaved.
const SELF_CONTAINED_KINDS = new Set(['title', 'displayTitle']);

export function runAssignmentWrite(userId, assignmentId, operation, isCurrentSession = () => true, kind = 'content', options = {}) {
  const key = keyFor(userId, assignmentId);
  const previous = lanes.get(key) || Promise.resolve(true);
  let transport;
  let transportSettled = false;
  let dispatched = false;
  const pending = withAssignmentRequestDeadline((signal, throwIfExpired) => {
    transport = previous.then(() => {
      throwIfExpired();
      if (!isCurrentSession()) throw new Error('The signed-in account changed. Reopen this draft.');
      dispatched = true;
      return operation(signal, throwIfExpired);
    }).finally(() => { transportSettled = true; });
    return transport;
  }, { ...options, abortOnTimeout: false, timeoutMessage: () => dispatched ? undefined
    : 'A previous assignment save is still in progress. Your changes have not been sent.' });
  // A UI deadline must not cancel a dispatched write: fetch could reject while
  // the server is still applying it. Keep queued writes behind its real result.
  const settled = Promise.all([
    Promise.resolve(transport).catch(() => {}),
    pending.then(() => true, () => false),
  ]).then(([, saved]) => saved || SELF_CONTAINED_KINDS.has(kind) ? previous : false);
  lanes.set(key, settled);
  settled.then(() => { if (lanes.get(key) === settled) lanes.delete(key); });
  return pending.catch((error) => {
    // A deadline is not a transport outcome. Let the owner observe the original
    // result; retries must not abandon this lane or forget a late acknowledgement.
    if (!transportSettled && dispatched) error.completion = transport;
    if (!dispatched) error.notDispatched = true;
    throw error;
  }).finally(() => {
    // Ordinary successes/failures have completed before their callers resume.
    // Timeouts remain bounded while their underlying transport continues.
    if (transportSettled) return settled.then(() => {});
  });
}

export async function drainAssignmentWrites(userId, assignmentId) {
  const key = keyFor(userId, assignmentId);
  let ok = true;
  try {
    await withAssignmentRequestDeadline(async (signal) => {
      while (!signal.aborted && lanes.has(key)) { if (!(await lanes.get(key))) ok = false; }
    });
  } catch (_) { ok = false; }
  return { ok };
}
